// src/store/reducers/attestationReducer.js
import { ATTESTATION_ACTIONS } from '../actions/actionTypes';

const initialState = {
  attestations: [],
  selectedAttestation: null,
  loading: false,
  error: null,
  success: null,
  documents: [],
  documentsMeta: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 1,
  },
  uploadProgress: 0,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    status: '',
    senderId: '',
    receiverId: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  stats: null,
  senderAttestations: [],
  senderMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  receiverAttestations: [],
  receiverMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  syncProgress: 0,
};

const attestationReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch attestations
    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        attestations: [],
      };

    // Fetch single attestation
    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedAttestation: action.payload,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedAttestation: null,
      };

    // Create attestation
    case ATTESTATION_ACTIONS.CREATE_ATTESTATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ATTESTATION_ACTIONS.CREATE_ATTESTATION_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: [action.payload, ...state.attestations],
        success: 'Attestation créée avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.CREATE_ATTESTATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update attestation
    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: state.attestations.map(att =>
          att.id === action.payload.id ? action.payload : att
        ),
        selectedAttestation: action.payload,
        success: 'Attestation mise à jour avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete attestation
    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: state.attestations.filter(att => att.id !== action.payload.id),
        success: action.payload.message || 'Attestation supprimée avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update attestation status
    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: state.attestations.map(att =>
          att.id === action.payload.id
            ? { ...att, status: action.payload.status }
            : att
        ),
        selectedAttestation: state.selectedAttestation?.id === action.payload.id
          ? { ...state.selectedAttestation, status: action.payload.status }
          : state.selectedAttestation,
        success: `Statut mis à jour: ${action.payload.status}`,
        error: null,
      };

    case ATTESTATION_ACTIONS.UPDATE_ATTESTATION_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Sync attestation
    case ATTESTATION_ACTIONS.SYNC_ATTESTATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        syncProgress: 0,
      };

    case ATTESTATION_ACTIONS.SYNC_ATTESTATION_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedAttestation: action.payload,
        syncProgress: 100,
        success: 'Attestation synchronisée avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.SYNC_ATTESTATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        syncProgress: 0,
      };

    // Bulk sync attestations
    case ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        attestations: state.attestations.map(att =>
          action.payload.ids.includes(att.id)
            ? { ...att, synched: true, synchedAt: new Date().toISOString() }
            : att
        ),
        success: action.payload.message || 'Attestations synchronisées avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.BULK_SYNC_ATTESTATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch attestation stats
    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        stats: null,
      };

    // Fetch attestations by sender
    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_SUCCESS:
      return {
        ...state,
        loading: false,
        senderAttestations: action.payload.data || [],
        senderMeta: action.payload.meta || initialState.senderMeta,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_SENDER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        senderAttestations: [],
      };

    // Fetch attestations by receiver
    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_SUCCESS:
      return {
        ...state,
        loading: false,
        receiverAttestations: action.payload.data || [],
        receiverMeta: action.payload.meta || initialState.receiverMeta,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATIONS_BY_RECEIVER_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        receiverAttestations: [],
      };

    // Export attestations
    case ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        success: 'Attestations exportées avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.EXPORT_ATTESTATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case ATTESTATION_ACTIONS.SET_ATTESTATION_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case ATTESTATION_ACTIONS.CLEAR_ATTESTATION_ERROR:
      return {
        ...state,
        error: null,
      };

    case ATTESTATION_ACTIONS.CLEAR_ATTESTATION_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case ATTESTATION_ACTIONS.CLEAR_SELECTED_ATTESTATION:
      return {
        ...state,
        selectedAttestation: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: action.payload.data || [],
        documentsMeta: action.payload.meta || { page: 1, limit: 12, total: 0, totalPages: 1 },
        error: null,
      };

    case ATTESTATION_ACTIONS.FETCH_ATTESTATION_DOCUMENTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        documents: [],
      };

    case ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: [action.payload, ...(state.documents || [])],
        uploadProgress: 100,
        success: 'Document téléchargé avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.UPLOAD_ATTESTATION_DOCUMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        uploadProgress: 0,
      };

    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        documents: state.documents.filter(doc => doc.id !== action.payload.documentId),
        success: action.payload.message || 'Document supprimé avec succès',
        error: null,
      };

    case ATTESTATION_ACTIONS.DELETE_ATTESTATION_DOCUMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ATTESTATION_ACTIONS.CLEAR_DOCUMENT_ERROR:
      return {
        ...state,
        error: null,
      };

    case ATTESTATION_ACTIONS.CLEAR_DOCUMENT_SUCCESS:
      return {
        ...state,
        success: null,
      };

    default:
      return state;
  }
};

export default attestationReducer;